import React from "react";
//HOOKS
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
//SLICES
import { selectLabels } from "../../slices/language";
import { selectUser } from "../../slices/user";
//UTILS
import { showError } from "../../utils/helpers";
//COMPONENTS
import DesktopHeader from "../../components/DesktopHeader";
import DesktopBody from "../../components/DesktopBody";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import { DesktopFiltersProps } from "../../components/DesktopFilters/DesktopFilters";
import DesktopDropView from "../../components/DesktopDropView";
import NewUserForm from "../../components/NewUserForm";
import DeleteUserModal from "../../components/Modals/DeleteUserModal";
import ResendConfirmationEmailModal from "../../components/Modals/ResendConfirmationEmailModal";
//API
import api, { CreateUserPayload } from "../../api";
//TYPES
import { User } from "../../api/models/user-model";
//MATERIAL
import { CircularProgress } from "@mui/material";
//DAYJS
import dayjs from "dayjs";

const HostUsers = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const headers = [
    { label: "ID", key: "ID" },
    { label: labels.name, key: labels.name },
    { label: labels.unit, key: labels.unit },
    { label: labels.email, key: labels.email },
    { label: labels.telephone, key: labels.telephone },
    { label: labels.role, key: labels.role },
    { label: labels.created_by, key: labels.created_by },
  ];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [newUser, setNewUser] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalUsers, setTotalUsers] = React.useState<number>(0);
  const [dataTable, setDataTable] = React.useState<any[]>([]);
  const [showDeleteUserModal, setShowDeleteUserModal] =
    React.useState<boolean>(false);
  const [userToResend, setUserToResend] = React.useState<User | null>(null);
  const [isResendingEmail, setIsResendingEmail] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null);
  const [shouldReloadUsers, setShouldReloadUsers] = React.useState(false);

  const debouncedSearchText = useDebounce(searchFilter, 500);

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const currentDate = dayjs().format("DD-MM-YY");

  const selectedPropertyName = user?.property?.name;

  const filters: DesktopFiltersProps = {
    filterData: [
      {
        type: "search",
        value: searchFilter,
        setValue: setSearchFilter,
        disabled: isLoading,
        tooltip: "",
        function: () => {},
        array: [],
        size: "small",
        icon: "",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
        filterWidth: 350,
      },
      {
        type: "function",
        value: "",
        setValue: () => {},
        disabled: isLoading,
        tooltip: labels.download_content,
        function: () => {},
        array: [],
        size: "small",
        icon: "fa-solid:file-download",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
        downloadable: true,
        downloadData: dataTable,
        downloadHeader: headers,
        downloadFilename: `${labels.users}-${selectedPropertyName} ${currentDate}`,
      },
      {
        type: "function",
        value: "",
        setValue: () => {},
        disabled: isLoading,
        tooltip: "",
        function: () => setNewUser(true),
        array: [],
        size: "small",
        icon: "fa6-solid:circle-plus",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
      },
    ],
  };

  const blockUser = React.useCallback(
    async (userId: number) => {
      try {
        const payload = {
          blocked: true,
        };
        const response = await api.updateUser(userId, payload);
        if (response) {
          setShouldReloadUsers(true);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    },
    [dispatch, labels.generic_error]
  );

  const unblockUser = React.useCallback(
    async (userId: number) => {
      try {
        const payload = {
          blocked: false,
        };
        const response = await api.updateUser(userId, payload);
        if (response) {
          setShouldReloadUsers(true);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    },
    [dispatch, labels.generic_error]
  );

  const getUsers = React.useCallback(
    async (debouncedSearchText: string) => {
      try {
        setIsLoading(true);
        const responseTotal = await api.usersListRequest(
          debouncedSearchText,
          user?.property?.id,
          null,
          "host",
          "secondary_user",
          "visitor"
        );
        const response = await api.usersListPaginationRequest(
          debouncedSearchText,
          user?.property?.id,
          null,
          "host",
          "secondary_user",
          "visitor",
          fixedPage,
          rowsPerPage
        );
        if (response && responseTotal) {
          setTotalUsers(responseTotal.length);
          const dataForDownloadableTable = response.map((item) => {
            return {
              ID: `${item?.id}`,
              [labels.name]: `${item?.name}`,
              [labels.unit]: `${item?.unit?.name}`,
              [labels.email]: `${item?.email}`,
              [labels.telephone]: `${item?.phone}`,
              [labels.role]: `${
                item?.role?.name === "host"
                  ? labels.main_user
                  : item?.role?.name === "secondary_user"
                  ? labels.secondary_user
                  : ""
              }`,
              [labels.created_by]: `${item?.created_by_users[0]?.name}`,
            };
          });
          setDataTable(dataForDownloadableTable);
          const dataForTable: TableData = {
            header: [
              {
                label: labels.name,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.unit,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.email,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.telephone,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.role,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.status,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: "",
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: "",
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: "",
                isIcon: false,
                tooltipLabel: "",
              },
            ],
            body: response.map((item) => {
              return {
                row: item.id,
                cells: [
                  {
                    label: [
                      {
                        mainLabel: item?.name,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-row",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.unit?.name,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-row",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.email,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-row",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.phone,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.role?.name.startsWith("host")
                          ? labels.main_user
                          : item?.role?.name.startsWith("secondary_user")
                          ? labels.secondary_user
                          : item?.role?.name.startsWith("visitor")
                          ? labels.recurrent_visitor
                          : "",
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.confirmed
                          ? item?.blocked
                            ? labels.blocked
                            : labels.active
                          : labels.pending,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                    color: item?.confirmed
                      ? item?.blocked
                        ? "#c2170e"
                        : "#15ab0f"
                      : "",
                    secondaryButton: !item?.confirmed
                      ? {
                          label: labels.resend_confirmation_email,
                          buttonFunction: () => setUserToResend(item),
                        }
                      : undefined,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.blocked
                          ? labels.unblock
                          : labels.block,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: true,
                    buttonFunction: () => {
                      if (item?.blocked) {
                        unblockUser(item?.id);
                      } else {
                        blockUser(item?.id);
                      }
                    },
                    disabledButton: !item?.confirmed,
                  },
                  {
                    label: [
                      {
                        mainLabel: "ic:round-edit",
                        tooltipLabel: labels.edit,
                      },
                    ],
                    isIcon: true,
                    display: "flex-dir-row",
                    isButton: false,
                    buttonFunction: () => {
                      setSelectedUser(item);
                      setNewUser(true);
                    },
                    disabledButton: !item?.confirmed,
                  },
                  {
                    label: [
                      {
                        mainLabel: "material-symbols-light:delete",
                        tooltipLabel: labels.delete,
                      },
                    ],
                    isIcon: true,
                    display: "flex-dir-row",
                    isButton: false,
                    buttonFunction: () => {
                      setUserToDelete(item);
                      setShowDeleteUserModal(true);
                    },
                    disabledButton: !item?.confirmed,
                  },
                ],
              };
            }),
          };
          setTableDataToDisplay(dataForTable);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      blockUser,
      dispatch,
      fixedPage,
      labels.active,
      labels.block,
      labels.blocked,
      labels.created_by,
      labels.delete,
      labels.edit,
      labels.email,
      labels.generic_error,
      labels.main_user,
      labels.name,
      labels.pending,
      labels.recurrent_visitor,
      labels.resend_confirmation_email,
      labels.role,
      labels.secondary_user,
      labels.status,
      labels.telephone,
      labels.unblock,
      labels.unit,
      rowsPerPage,
      unblockUser,
      user?.property?.id,
    ]
  );

  React.useEffect(() => {
    if (user?.property?.id) {
      getUsers(debouncedSearchText);
    }
  }, [debouncedSearchText, getUsers, user?.property?.id]);

  React.useEffect(() => {
    if (shouldReloadUsers) {
      getUsers(debouncedSearchText);
      setShouldReloadUsers(false);
    }
  }, [debouncedSearchText, getUsers, shouldReloadUsers]);

  const deleteUser = async () => {
    if (userToDelete) {
      try {
        setIsLoading(true);
        const formattedDate = dayjs().format("YYYY-MM-DD");
        const payload: CreateUserPayload = {
          deleted_at: formattedDate,
        };
        const response = await api.updateUser(userToDelete?.id, payload);
        if (response) {
          setShowDeleteUserModal(false);
          setUserToDelete(null);
          getUsers(debouncedSearchText);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const resendConfirmationEmail = async () => {
    if (!userToResend?.email) return;

    try {
      setIsResendingEmail(true);
      await api.resendConfirmationEmail({
        email: userToResend.email,
      });
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      throw error;
    } finally {
      setIsResendingEmail(false);
    }
  };

  return (
    <div className="w-100">
      <DeleteUserModal
        showDeleteUserModal={showDeleteUserModal}
        setShowDeleteUserModal={setShowDeleteUserModal}
        deleteFunction={deleteUser}
        userToDelete={userToDelete}
        setUserToDelete={setUserToDelete}
      />
      <ResendConfirmationEmailModal
        open={!!userToResend}
        onClose={() => setUserToResend(null)}
        onConfirm={resendConfirmationEmail}
        user={userToResend}
        isLoading={isResendingEmail}
      />
      <DesktopHeader title={labels.users} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <TableComponent
              selectableRow={false}
              data={tableDataToDisplay}
              onPressRow={() => {}}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              total={totalUsers}
              tableHeight={"calc(100vh - 188px)"}
              emptyMessage={labels.no_users_registered}
            />
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={newUser}
        setShowView={setNewUser}
        content={
          <div className="flex h-100 border-box">
            <div className="h-100 border-box p-8 scroll">
              <NewUserForm
                roleToCreate={3}
                showForm={newUser}
                setShowForm={setNewUser}
                extraFunc={() => getUsers(debouncedSearchText)}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                isMobile={false}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={selectedUser ? labels.edit_user : labels.new_user}
      />
    </div>
  );
};

export default HostUsers;
